import React from "react";
import { Box, Typography } from "../../../design-system";
import UploadDownloadArea from "./UploadDownloadArea";
import HeroClipDemo from "./HeroClipDemo";
import EtDemo from "./EtDemo";

/**
 * Variant G — a full landing/home hybrid (one scrolling page): hero → "your footage as a
 * spreadsheet" proof → features → Black Cherry AI → how it works (lightweight instructions) →
 * final CTA. Reuses the REAL upload box (UploadDownloadArea) and looping demo (HeroClipDemo).
 *
 * Honest messaging only: CherryClipz transcribes/cuts in the cloud (AssemblyAI), so we do NOT
 * claim "local-first / nothing uploaded" (that mockup claim is false for this app).
 *
 * Copy is driven by `copySet` (0–2) so the hero headline/subhead/CTA can be toggled.
 */

export const COPY_SETS = [
  {
    eyebrow: "VIDEO → A SPREADSHEET OF CLIPS",
    h1a: "One video in.",
    h1b: "A hundred clips out.",
    sub: "CherryClipz cuts every line into its own clip and tags it — you cherry-pick the keepers, or let AI pick for you.",
  },
  {
    eyebrow: "YOU PICK · OR AI PICKS",
    h1a: "Don’t let AI guess.",
    h1b: "Cherry-pick the good ones.",
    sub: "Every sentence becomes a searchable, scorable clip. Keep full control — or hand it to Black Cherry AI. Your call, every time.",
  },
  {
    eyebrow: "STOP SCRUBBING THE TIMELINE",
    h1a: "Find every great moment.",
    h1b: "In minutes, not hours.",
    sub: "Drop a video — get every quote as a clip you can filter, score, and export. Or just ask AI to build the cut for you.",
  },
];

const PINK = "#e61d50";

const Section: React.FC<{ children: React.ReactNode; maxWidth?: number; pad?: boolean }> = ({ children, maxWidth = 1180, pad = true }) => (
  <Box sx={{ width: "100%", maxWidth: `${maxWidth}px`, mx: "auto", px: { xs: "16px", md: "24px" }, py: pad ? { xs: "44px", md: "72px" } : 0 }}>
    {children}
  </Box>
);

const SecTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, letterSpacing: "0.22em", fontSize: "12px", color: PINK, textTransform: "uppercase" }}>
    {children}
  </Typography>
);

const SecHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, letterSpacing: "-0.02em", color: "#f3f4f9", fontSize: { xs: "28px", md: "46px" }, lineHeight: 1.04, mt: "12px" }}>
    {children}
  </Typography>
);

const SecSub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, color: "#9aa0b4", fontSize: { xs: "15px", md: "17px" }, lineHeight: 1.6, mt: "14px", maxWidth: "640px" }}>
    {children}
  </Typography>
);

const FEATURES = [
  { t: "One clip per sentence", d: "We cut a clip the moment each line starts and stops — every quote, plus the silences in between, ready to keep or drop." },
  { t: "Tagged like language", d: "Each clip arrives labelled with speaker, emotion, questions, verbs, adjectives and nouns — plus duration and length. Meaning on every row." },
  { t: "Filter & sort anything", d: "Slice by sentiment, speaker, time, length or words. Full-text search, sort, reorder columns — even translate." },
  { t: "Black Cherry AI", d: "Brief the AI in plain words and it picks clips for you, checked straight into the grid. You keep the final say." },
  { t: "Trim & export", d: "Nudge each clip’s in/out, then download a single clip or export the whole sequence stitched into one video." },
  { t: "Share an ET", d: "Send anyone a link to your clip sheet — they can browse, score with AI, and grab clips without an account." },
];

const STEPS = [
  { n: "1", t: "Upload or paste a link", d: "Drop a video/audio file, or paste a YouTube link. We transcribe and cut it for you." },
  { n: "2", t: "Every line becomes a clip", d: "Your footage lands as a sortable spreadsheet — one selectable clip per sentence, fully tagged." },
  { n: "3", t: "Cherry-pick — or ask AI", d: "Filter, score, and tick the keepers yourself, or let Black Cherry choose. You stay in control." },
  { n: "4", t: "Export", d: "Download a single clip or stitch your picks into one video, in the order you chose." },
];

const FullHybridPage: React.FC<{ copySet?: number; onTrySample?: () => void }> = ({ copySet = 0, onTrySample }) => {
  const c = COPY_SETS[copySet] || COPY_SETS[0];
  const scrollToUpload = () => {
    try { document.getElementById("cc-upload")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* ignore */ }
  };
  return (
    <Box sx={{ width: "100%", fontFamily: "Outfit, sans-serif" }}>
      {/* ───────── HERO ───────── */}
      <Section>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: "32px", md: "56px" }, alignItems: "center" }}>
          <Box sx={{ flex: "1 1 48%", display: "flex", flexDirection: "column", gap: { xs: "18px", md: "22px" }, alignItems: { xs: "center", md: "flex-start" }, textAlign: { xs: "center", md: "left" } }}>
            <SecTag>{c.eyebrow}</SecTag>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.02, color: "#f3f4f9", fontSize: { xs: "40px", md: "68px" } }}>
              {c.h1a}
              <Box component="span" sx={{ display: "block", color: PINK }}>{c.h1b}</Box>
            </Typography>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, color: "#c2c7d6", fontSize: { xs: "16px", md: "20px" }, lineHeight: 1.5, maxWidth: "560px" }}>
              {c.sub}
            </Typography>
            <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: { xs: "center", md: "flex-start" }, mt: "2px" }}>
              <Box component="button" onClick={onTrySample}
                sx={{ cursor: "pointer", border: "none", borderRadius: "999px", backgroundColor: PINK, color: "#fff", fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "16px", padding: "13px 26px", "&:hover": { backgroundColor: "#c4123f" } }}>
                ▶ Try a sample reel
              </Box>
              <Box component="button" onClick={scrollToUpload}
                sx={{ cursor: "pointer", backgroundColor: "transparent", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.18)", color: "#f3f4f9", fontFamily: "Outfit, sans-serif", fontWeight: 600, fontSize: "16px", padding: "13px 26px", display: "inline-flex", alignItems: "center", "&:hover": { borderColor: PINK } }}>
                Upload your video
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 10px", justifyContent: { xs: "center", md: "flex-start" }, mt: "4px" }}>
              {["Speaker", "Emotion", "Time", "AI scoring", "+ more"].map((p) => (
                <Box key={p} sx={{ display: "flex", alignItems: "center", gap: "7px", padding: "5px 12px", borderRadius: "999px", border: "1px solid rgba(230,29,80,0.32)", backgroundColor: "rgba(230,29,80,0.06)" }}>
                  <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: PINK }} />
                  <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, color: "#c2c7d6", fontSize: "13px", whiteSpace: "nowrap" }}>{p}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {/* The product, playing — proof beside the pitch */}
          <Box sx={{ flex: "1 1 52%", width: "100%", borderRadius: "16px", border: "1px solid rgba(230,29,80,0.22)", background: "radial-gradient(120% 100% at 50% 0%, rgba(230,29,80,0.10), transparent 60%)", padding: { xs: "10px", md: "14px" } }}>
            <HeroClipDemo />
          </Box>
        </Box>
      </Section>

      {/* ───────── UPLOAD ───────── */}
      <Box id="cc-upload" sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.015)" }}>
        <Section>
          <Box sx={{ textAlign: "center", mb: "24px" }}>
            <SecTag>Start now</SecTag>
            <SecHead>Drop a video. Get your clips.</SecHead>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <UploadDownloadArea />
          </Box>
          <Typography sx={{ textAlign: "center", color: "#8d94a8", fontSize: "13px", mt: "16px" }}>
            No signup needed to try — longer videos take a little longer to process.
          </Typography>
        </Section>
      </Box>

      {/* ───────── THE SPREADSHEET (wider — the ET needs room so nothing's cut off) ───────── */}
      <Section maxWidth={1560}>
        <SecTag>The workspace — try it live</SecTag>
        <SecHead>Your footage, as a spreadsheet</SecHead>
        <SecSub>This is the real editor with sample clips. Every sentence is a row; every quality is a column. <Box component="span" sx={{ color: "#f3f4f9", fontWeight: 600 }}>Go ahead — filter, search, sort, tick clips, trim them, and open Black Cherry / Segment / Export.</Box> The guide on the right teaches itself — hover any button, or just watch the tips.</SecSub>
        <Box sx={{ mt: "28px" }}>
          <EtDemo />
        </Box>
      </Section>

      {/* ───────── FEATURES ───────── */}
      <Box sx={{ background: "rgba(255,255,255,0.015)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Section>
          <SecTag>What you get</SecTag>
          <SecHead>Built like a database, not a timeline</SecHead>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: "16px", mt: "28px" }}>
            {FEATURES.map((f) => (
              <Box key={f.t} sx={{ borderRadius: "14px", border: "1px solid rgba(255,255,255,0.08)", background: "linear-gradient(160deg, rgba(27,21,36,0.6), rgba(21,16,28,0.6))", padding: "22px" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: PINK, mb: "14px" }} />
                <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, color: "#f3f4f9", fontSize: "17px", mb: "8px" }}>{f.t}</Typography>
                <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 400, color: "#9aa0b4", fontSize: "14px", lineHeight: 1.58 }}>{f.d}</Typography>
              </Box>
            ))}
          </Box>
        </Section>
      </Box>

      {/* ───────── BLACK CHERRY ───────── */}
      <Section>
        <Box sx={{ borderRadius: "20px", border: "1px solid rgba(255,255,255,0.08)", background: "radial-gradient(120% 140% at 85% -10%, rgba(230,29,80,0.16), transparent 55%), linear-gradient(160deg, rgba(27,21,36,0.7), rgba(21,16,28,0.7))", padding: { xs: "28px", md: "44px" } }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: "24px", md: "40px" }, alignItems: "center" }}>
            <Box sx={{ flex: "1 1 55%" }}>
              <SecTag>Black Cherry AI</SecTag>
              <SecHead>Tell it how to edit. It picks for you.</SecHead>
              <SecSub>Describe the cut you want — “a punchy 30-second hype reel, no filler, end on the loudest line” — and Black Cherry selects the clips and checks them into your grid. Don’t want to write a prompt? One click scores every clip for virality. You always keep the final say.</SecSub>
            </Box>
            <Box sx={{ flex: "1 1 45%", width: "100%", borderRadius: "14px", border: "1px solid rgba(255,255,255,0.10)", background: "rgba(12,11,16,0.6)", padding: "18px" }}>
              <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#8d94a8", mb: "10px" }}>Brief Black Cherry</Typography>
              <Typography sx={{ fontFamily: "Outfit, sans-serif", color: "#f3f4f9", fontSize: "15px", lineHeight: 1.5, borderLeft: `2px solid ${PINK}`, pl: "12px" }}>
                make me a 30s hype reel — only the strongest lines, no filler, end on the loudest moment
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "7px", mt: "16px" }}>
                {["00:03→00:05", "00:07→00:09", "00:14→00:17", "00:17→00:22"].map((p) => (
                  <Typography key={p} sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#c2c7d6", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "7px", padding: "5px 9px" }}>● {p}</Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Section>

      {/* ───────── HOW IT WORKS (lightweight instructions) ───────── */}
      <Box sx={{ background: "rgba(255,255,255,0.015)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Section>
          <SecTag>How it works</SecTag>
          <SecHead>From upload to export in four steps</SecHead>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: "16px", mt: "28px" }}>
            {STEPS.map((s) => (
              <Box key={s.n} sx={{ borderRadius: "14px", border: "1px solid rgba(255,255,255,0.08)", padding: "22px" }}>
                <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "rgba(230,29,80,0.14)", color: PINK, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "15px", mb: "12px" }}>{s.n}</Box>
                <Typography sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, color: "#f3f4f9", fontSize: "16px", mb: "6px" }}>{s.t}</Typography>
                <Typography sx={{ fontFamily: "Outfit, sans-serif", color: "#9aa0b4", fontSize: "13.5px", lineHeight: 1.55 }}>{s.d}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: "22px" }}>
            <Box component="button"
              onClick={() => window.open("https://www.youtube.com/playlist?list=PLCmEY2bUooBzva-gvuh3TpOpWoHjuRMqm", "_blank", "noopener,noreferrer")}
              sx={{ cursor: "pointer", background: "transparent", border: "none", padding: 0, color: PINK, fontFamily: "Outfit, sans-serif", fontWeight: 600, fontSize: "15px", "&:hover": { textDecoration: "underline" } }}>
              Watch the full walkthrough in the Learning Center →
            </Box>
          </Box>
        </Section>
      </Box>

      {/* ───────── FINAL CTA ───────── */}
      <Section>
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "18px" }}>
          <SecHead>Ready to cherry-pick your clips?</SecHead>
          <SecSub>Upload a video, or try it on a sample reel — no signup to start.</SecSub>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center", mt: "6px" }}>
            <Box component="button" onClick={onTrySample}
              sx={{ cursor: "pointer", border: "none", borderRadius: "999px", backgroundColor: PINK, color: "#fff", fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "16px", padding: "13px 28px", "&:hover": { backgroundColor: "#c4123f" } }}>
              ▶ Try a sample reel
            </Box>
            <Box component="button" onClick={scrollToUpload}
              sx={{ cursor: "pointer", backgroundColor: "transparent", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.18)", color: "#f3f4f9", fontFamily: "Outfit, sans-serif", fontWeight: 600, fontSize: "16px", padding: "13px 28px", display: "inline-flex", alignItems: "center", "&:hover": { borderColor: PINK } }}>
              Upload your video
            </Box>
          </Box>
        </Box>
      </Section>
    </Box>
  );
};

export default FullHybridPage;
